// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridValidRowModel,
  GridRowIdGetter,
  GridFilterModel,
  GridCallbackDetails,
  GridSortModel,
  GridEventListener,
} from '@mui/x-data-grid';
import { dataGridSx } from './dataGridSx';
import { withScrollableCell } from './withScrollableCell';

type DataTableProps<R extends GridValidRowModel> = {
  columns: GridColDef<R>[];
  rows: R[];
  rowKey: GridRowIdGetter<R>;
  toolbar?: React.FC<{ selected: R[] }>;
  onRowClick?: GridEventListener<'rowClick'> | undefined;
  onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  defaultSortModel?: GridSortModel;
  disableVirtualization?: boolean;
};

export const DataTable = <R extends GridValidRowModel>({
  columns,
  rows,
  rowKey,
  toolbar,
  onFilterModelChange,
  defaultSortModel,
  onRowClick,
  disableVirtualization,
}: DataTableProps<R>): JSX.Element => {
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel || []);

  // Use the withScrollableCell utility to wrap columns
  const scrollableColumns = withScrollableCell(columns);
  return (
    <DataGrid
      onRowClick={onRowClick}
      disableRowSelectionOnClick
      disableVirtualization={!!disableVirtualization}
      sx={(theme) => dataGridSx(theme, !!onRowClick)}
      columns={scrollableColumns} // Use scrollable columns
      rows={rows}
      getRowId={rowKey}
      slots={{ toolbar }}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      rowSelection={false}
    />
  );
};
